import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ExperienceSection = () => {
  const experiences = [
    {
      id: 1,
      company: "Lensta Tech Pvt Ltd(Givfin)",
      logo: "https://images.crunchbase.com/image/upload/c_pad,h_160,w_160,f_auto,b_white,q_auto:eco,dpr_2/eehvupwlfjq1pyy42u2h",
      position: " Software Engineer",
      duration: "Nov 2021 - Oct 2023",
      location: "Bangalore, IN",
      type: "Full-time",
      responsibilities: [
        "Managed development of the Loan Origination System (LOS) in a full-stack capacity, overseeing the end-to-end project lifecycle.",
        "Created and maintained an intuitive user interface using React, improving user experience and accessibility.",
        "Constructed backend logic with Node.js and Koa.js, enhancing database communication and system performance.",
        "Guided the LOS lifecycle from requirements gathering to design, testing, deployment, and ongoing maintenance.",
        "Secured system stability and reliability through prompt bug resolution and performance enhancements."
      ],
      technologies: ["React", "Node.js","Koa.js", "MySQL", "AWS"],
      // achievements: "Increased application performance by 40% and reduced server costs by 25%"
    },
    {
      id: 2,
      company: "KIMO AI LLP ",
      logo: "https://media.licdn.com/dms/image/v2/C4E0BAQHyTSQFemLSwA/company-logo_200_200/company-logo_200_200/0/1632327045929/kimo_ai_logo?e=1759363200&v=beta&t=WAOTSar6xl01CmMVj4E1zEkuXi8PM0X0I-LQveGOhbs",
      position: "Software Engineer",
      duration: "Sept 2024 - Dec 2024",
      location: "Remote",
      type: "Full-time",
      responsibilities: [
        "Enhanced backend architecture using Python and FastAPI, enabling efficient handling of API requests and improving system performance",
        "Implemented real-time push notifications for user applications, increasing user engagement by 25% during live events.",
        "Implemented real-time push notifications for user applications, increasing user engagement by 25% during live events."
      ],
      technologies: ["Python", "Next.js", "FastAPI", "Mongodb", "Firebase"],
      // achievements: "Successfully launched product that acquired 5K+ users in first 6 months"
    },
  ];

  return (
    <section id="experience" className="py-20 bg-background">
      <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center space-y-4 mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground">
            Work Experience
          </h2>
          <div className="w-20 h-1 bg-primary rounded-full mx-auto"></div>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            My professional journey building scalable applications and leading development teams
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-4 md:left-1/2 transform md:-translate-x-px top-0 bottom-0 w-0.5 bg-border"></div>

          {/* Experience Items */}
          <div className="space-y-12">
            {experiences?.map((exp, index) => (
              <div
                key={exp?.id}
                className={`relative flex items-center ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                } flex-col md:space-x-8`}
              >
                {/* Timeline Dot */}
                <div className="absolute left-4 md:left-1/2 transform md:-translate-x-1/2 w-4 h-4 bg-primary rounded-full border-4 border-background shadow-elevation-1 z-10"></div>

                {/* Content Card */}
                <div className={`w-full md:w-1/2 ml-12 md:ml-0 ${index % 2 === 0 ? 'md:pr-8' : 'md:pl-8'}`}>
                  <div className="bg-card rounded-xl p-6 shadow-elevation-1 hover:shadow-elevation-2 transition-all duration-300 animate-fade-in">
                    {/* Company Header */}
                    <div className="flex items-start space-x-4 mb-4">
                      <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-muted">
                        <Image
                          src={exp?.logo}
                          alt={`${exp?.company} logo`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-foreground">{exp?.position}</h3>
                        <p className="text-primary font-semibold">{exp?.company}</p>
                        <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Icon name="Calendar" size={14} />
                            {exp?.duration}
                          </span>
                          <span className="flex items-center gap-1">
                            <Icon name="MapPin" size={14} />
                            {exp?.location}
                          </span>
                          <span className="bg-muted px-2 py-1 rounded-full text-xs">
                            {exp?.type}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Responsibilities */}
                    <div className="mb-4">
                      <h4 className="font-semibold text-foreground mb-2">Key Responsibilities:</h4>
                      <ul className="space-y-1">
                        {exp?.responsibilities?.map((responsibility, idx) => (
                          <li key={idx} className="flex items-start space-x-2 text-sm text-muted-foreground">
                            <Icon name="ChevronRight" size={14} className="mt-0.5 flex-shrink-0 text-primary" />
                            <span>{responsibility}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Technologies */}
                    <div className="mb-4">
                      <h4 className="font-semibold text-foreground mb-2">Technologies Used:</h4>
                      <div className="flex flex-wrap gap-2">
                        {exp?.technologies?.map((tech, idx) => (
                          <span
                            key={idx}
                            className="bg-primary/10 text-primary px-3 py-1 rounded-full text-xs font-medium"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Achievements */}
                    {exp?.achievements && (
                    <div className="bg-muted/50 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <Icon name="Trophy" size={16} className="text-accent mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="font-semibold text-foreground text-sm mb-1">Key Achievement:</h4>
                          <p className="text-sm text-muted-foreground">{exp?.achievements}</p>
                        </div>
                      </div>
                    </div>
                    )}
                  </div>
                </div>

                {/* Empty space for alternating layout */}
                <div className="hidden md:block w-1/2"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Experience Summary */}
        <div className="mt-16 bg-gradient-to-r from-primary/5 to-accent/5 rounded-2xl p-8 text-center">
          <h3 className="text-2xl font-bold text-foreground mb-4">
            Ready to bring this experience to your team
          </h3>
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
            With 2+ years of hands-on experience in full-stack development, I'm passionate about 
            building scalable solutions and helping businesses achieve their goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => {
                const contactSection = document.getElementById('contact');
                if (contactSection) {
                  contactSection?.scrollIntoView({ 
                    behavior: 'smooth',
                    block: 'start'
                  });
                }
              }}
              className="bg-primary text-white px-6 py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors duration-200 flex items-center justify-center gap-2"
            >
              <Icon name="MessageCircle" size={18} />
              Let's Talk
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExperienceSection;